import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import PlusMinQty from "../../../utilities/PlusMinQty";
import { useDispatch } from "react-redux";
import { updatePositionAction } from "../../../../redux/actions/brokerAction";
import { X } from "lucide-react";
import { useSelector } from "react-redux";
import { fmtINR, calculatePositionUnrealisedPnl } from "../../../../util/helper";

const defSLTP = ["Price", "PNL", "Underlying"];

const parseNumeric = (str) => {
  if (!str) return 0;
  const cleanStr = str.replace(/[^\d.-]/g, '');
  return parseFloat(cleanStr) || 0;
};

const OPERATOR_OPTIONS = [
  { value: 0, label: ">" },
  { value: 1, label: ">=" },
  { value: 2, label: "<" },
  { value: 3, label: "<=" },
];
const operatorLabel = (op) => OPERATOR_OPTIONS.find(o => o.value === op)?.label || ">";
const UNDERLYING_TYPES = [
  { value: 0, label: "Spot" },
  { value: 1, label: "Fut" },
  { value: 2, label: "SynFut" },
];

const LegSLTPModal = ({ show, setShow, rowData, onSave }) => {
  const dispatch = useDispatch();
  const sockSource = useSelector((state) => state.sockSource?.source);
  const [formData, setFormData] = useState({
    sltpType: "PNL",
    stopLossPrice: null,
    targetPrice: null,
    trailReferencePoints: null,
    trailStepPoints: null,
    profitLockAt: null,
    profitLockSl: null,
    profitLockTrailReferencePoints: null,
    profitLockTrailStepPoints: null,
  });
  const [isError, setError] = useState("");
  const [errorMessages, setErrorMessages] = useState({
    stopLoss: "",
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  const [slValueType, setSlValueType] = useState(0);
  const [tpValueType, setTpValueType] = useState(0);
  const [trailValueType, setTrailValueType] = useState(2);
  const [ltp, setLtp] = useState(rowData?.ltp || 0);
  const [slOperator, setSlOperator] = useState(2);
  const [targetOperator, setTargetOperator] = useState(0);
  const [underlyingType, setUnderlyingType] = useState(0);

  const valuRef = useRef();

  const calculateActualPrice = (value, valueType, basePrice, isStopLoss = false) => {
    if (value === null || value === undefined || value === '' || value === 0) return null;
    const val = parseFloat(value);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    const isLongPosition = rowData?.netQuantity > 0;
    let result;

    switch (valueType) {
      case 0:
        result = val;
        break;
      case 1:
        if (formData.sltpType === "Price") {
          if (isStopLoss) {
            result = isLongPosition ? base - (base * val / 100) : base + (base * val / 100);
          } else {
            result = isLongPosition ? base + (base * val / 100) : base - (base * val / 100);
          }
        } else {
          result = base + (base * val / 100);
        }
        break;
      case 2:
        if (formData.sltpType === "Price") {
          if (isStopLoss) {
            result = isLongPosition ? base - val : base + val;
          } else {
            result = isLongPosition ? base + val : base - val;
          }
        } else if (formData.sltpType === "PNL") {
          result = isStopLoss ? base - val : base + val;
        } else {
          result = isStopLoss ? -val : val;
        }
        break;
      default:
        result = val;
    }

    return result;
  };

  useEffect(() => {
    if (show && rowData?.instrumentToken && sockSource) {
      const subscription = sockSource
        .subscribe("" + rowData.instrumentToken, (message) => {
          const data = message?.data;
          if (data && data.lp !== undefined) {
            const newLtp = parseFloat(data.lp);
            setLtp(newLtp);
            const ltpId = `ltp-${rowData.instrumentToken}-${rowData.basketId || ''}`;
            const ltpEl = document.getElementById(ltpId);
            if (ltpEl) {
              ltpEl.innerText = newLtp?.toFixed(2);
            }

            const pnlId = `pnl-${rowData.instrumentToken}-${rowData.basketId || ''}`;
            const pnlEl = document.getElementById(pnlId);
            if (pnlEl) {
              const newPnl = calculatePositionUnrealisedPnl(rowData, newLtp);
              pnlEl.innerText = fmtINR(newPnl);
              pnlEl.className = parseFloat(newPnl) >= 0 ? "text-success" : "text-danger";
            }
          }
        })
        .on("unsubscribe", () => { })
        .on("subscribe", () => { })
        .on("error", (err) => {
          console.log("Error subscribing to LTP updates for token:", rowData.instrumentToken, err);
        });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [show, rowData?.instrumentToken, rowData?.basketId, sockSource]);

  useEffect(() => {
    if (rowData) {
      const basisIsPNL = rowData.rms?.basis === 0;

      const stopLossInputValue = rowData.rms?.stopLossInput?.value;
      const targetInputValue = rowData.rms?.targetInput?.value;
      const trailRefValue = rowData.rms?.trailSL?.reference;
      const trailStepValue = rowData.rms?.trailSL?.step;

      const value = {
        sltpType: rowData.rms?.basis === 2 ? "Underlying" : (rowData.rms?.basis === 0 ? "PNL" : "Price"),
        stopLossPrice: stopLossInputValue !== null && stopLossInputValue !== undefined ? parseFloat(stopLossInputValue).toFixed(2) : null,
        targetPrice: targetInputValue !== null && targetInputValue !== undefined ? parseFloat(targetInputValue).toFixed(2) : null,
        trailReferencePoints: trailRefValue !== null && trailRefValue !== undefined ? parseFloat(trailRefValue).toFixed(2) : null,
        trailStepPoints: trailStepValue !== null && trailStepValue !== undefined ? parseFloat(trailStepValue).toFixed(2) : null,
        profitLockAt: rowData.rms?.profitLockAt ? parseFloat(rowData.rms?.profitLockAt.toFixed ? rowData.rms?.profitLockAt.toFixed(2) : rowData.rms?.profitLockAt).toFixed(2) : null,
        profitLockSl: rowData.rms?.profitLockSl ? parseFloat(rowData.rms?.profitLockSl.toFixed ? rowData.rms?.profitLockSl.toFixed(2) : rowData.rms?.profitLockSl).toFixed(2) : null,
        profitLockTrailReferencePoints: rowData.rms?.profitLockTrail?.reference ? parseFloat(rowData.rms?.profitLockTrail?.reference.toFixed ? rowData.rms?.profitLockTrail?.reference.toFixed(2) : rowData.rms?.profitLockTrail?.reference).toFixed(2) : null,
        profitLockTrailStepPoints: rowData.rms?.profitLockTrail?.step ? parseFloat(rowData.rms?.profitLockTrail?.step.toFixed ? rowData.rms?.profitLockTrail?.step.toFixed(2) : rowData.rms?.profitLockTrail?.step).toFixed(2) : null,
      };

      valuRef.current = value;
      setFormData(value);
      setError("");
      setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });

      if (basisIsPNL) {
        const savedSlValueType = rowData.rms?.stopLossInput?.valueType ?? 0;
        const savedTpValueType = rowData.rms?.targetInput?.valueType ?? 0;

        setSlValueType(savedSlValueType);
        setTpValueType(savedTpValueType);
        setTrailValueType(2);
      } else {
        const savedSlValueType = rowData.rms?.stopLossInput?.valueType ?? 0;
        const savedTpValueType = rowData.rms?.targetInput?.valueType ?? 0;
        const savedTrailValueType = rowData.rms?.trailSL?.valueType ?? 2;


        setSlValueType(savedSlValueType);
        setTpValueType(savedTpValueType);
        setTrailValueType(savedTrailValueType);
      }

      setLtp(rowData?.ltp || 0);

      const basisInit = rowData.rms?.basis === 2 ? "Underlying" : (rowData.rms?.basis === 0 ? "PNL" : "Price");
      if (basisInit === "Underlying") {
        setSlValueType(0);
        setTpValueType(0);
        setTrailValueType(2);
        setSlOperator(rowData.rms?.stopLossOperator ?? 2);
        setTargetOperator(rowData.rms?.targetOperator ?? 0);
        setUnderlyingType(rowData.rms?.underlyingType ?? 0);
      }
    }
  }, [rowData]);

  const handleReset = () => {
    if (rowData) {
      const resetFormData = {
        sltpType: rowData.rms?.basis === 0 ? "PNL" : "Price",
        stopLossPrice: null,
        targetPrice: null,
        trailReferencePoints: null,
        trailStepPoints: null,
        profitLockAt: null,
        profitLockSl: null,
        profitLockTrailReferencePoints: null,
        profitLockTrailStepPoints: null,
      };
      setFormData(resetFormData);
      setError("");
      setErrorMessages({
        stopLoss: "",
        target: "",
        trailSL: "",
        profitLock: "",
        profitLockTrail: ""
      });
      setSlValueType(0);
      setTpValueType(0);
      setTrailValueType(2);
      setSlOperator(2);
      setTargetOperator(0);
      setUnderlyingType(0);
    }
  };

  const isValidsltp = (type, val, overrideType = null) => {
    const valueType = overrideType ?? (type === "sl" ? slValueType : tpValueType);
    const setMsg = (ok, customMsg = null) => {
      if (type === "sl") {
        const msg = customMsg || (formData.sltpType === "PNL" ? "Stop Loss should be below current PNL" : (rowData.netQuantity > 0 ? "Stop Loss should be below current price" : "Stop Loss should be above current price"));
        setErrorMessages((prev) => ({ ...prev, stopLoss: ok ? "" : msg }));
      } else {
        const msg = customMsg || (formData.sltpType === "PNL" ? "Target should be above current PNL" : (rowData.netQuantity > 0 ? "Target should be above current price" : "Target should be below current price"));
        setErrorMessages((prev) => ({ ...prev, target: ok ? "" : msg }));
      }
    };

    if (val === null || val === undefined || val === '' || val === 0) {
      setMsg(true);
      setError("");
      return true;
    }

    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setMsg(true);
      setError("");
      return true;
    }

    const actualPrice = type === "sl"
      ? calculateActualPrice(parsedVal, valueType, getBasePrice(), true)
      : calculateActualPrice(parsedVal, valueType, getBasePrice(), false);

    if (actualPrice === null) {
      setMsg(true);
      setError("");
      return true;
    }

    if (formData.sltpType === "Underlying") {
      if (parsedVal <= 0) {
        setMsg(false, type === "sl" ? "Stop Loss price must be positive" : "Target price must be positive");
        setError("");
        return false;
      }
      setMsg(true);
      setError("");
      return true;
    }

    if (formData.sltpType === "Price") {
      if (actualPrice <= 0) {
        setMsg(false, type === "sl" ? "Stop Loss must be positive" : "Target must be positive");
        setError("");
        return false;
      }
    }

    if (formData.sltpType === "PNL") {
      if (valueType === 2 && val < 0) {
        setMsg(false, type === "sl" ? "Stop Loss cannot be negative" : "Target cannot be negative");
        setError("");
        return false;
      }
    }

    const currentBasePrice = getBasePrice();

    if (formData.sltpType === "PNL") {
      const pnl = currentBasePrice;

      if (Number.isNaN(pnl)) {
        setMsg(true);
        setError("");
        return true;
      }

      if (type === "sl") {
        const ok = actualPrice < pnl;
        setError(ok ? "" : type);
        setMsg(ok);
        return ok;
      } else {
        const ok = actualPrice > pnl;
        setError(ok ? "" : type);
        setMsg(ok);
        return ok;
      }
    }

    const currentLtp = currentBasePrice;

    if (Number.isNaN(currentLtp) || currentLtp === 0) {
      setMsg(true);
      setError("");
      return true;
    }

    if (type === "sl") {
      if (rowData.netQuantity > 0) {
        const ok = actualPrice < currentLtp;
        setError(ok ? "" : type);
        setMsg(ok);
        return ok;
      } else {
        const ok = actualPrice > currentLtp;
        setError(ok ? "" : type);
        setMsg(ok);
        return ok;
      }
    } else {
      if (rowData.netQuantity > 0) {
        const ok = actualPrice > currentLtp;
        setError(ok ? "" : type);
        setMsg(ok);
        return ok;
      } else {
        const ok = actualPrice < currentLtp;
        setError(ok ? "" : type);
        setMsg(ok);
        return ok;
      }
    }
  };

  const currentPnl = React.useMemo(() => {
    if (!rowData) return 0;
    if (!ltp) return parseFloat(rowData.unrealizedPNL || 0);
    return parseFloat(calculatePositionUnrealisedPnl(rowData, ltp));
  }, [rowData, ltp]);

  const getBasePrice = () => {
    const ltpId = "ltp-" + rowData?.instrumentToken + "-" + (rowData?.basketId || '');
    const ltpEl = document.getElementById(ltpId);
    const effectiveLtp = ltpEl ? (parseNumeric(ltpEl.innerText) || ltp) : ltp;

    if (formData.sltpType === "PNL") {
      return parseFloat(calculatePositionUnrealisedPnl(rowData, effectiveLtp));
    } else {
      return effectiveLtp;
    }
  };

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };
  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");

  const basePrice = getBasePrice();
  const slActualPrice = React.useMemo(() =>
    calculateActualPrice(formData.stopLossPrice, slValueType, basePrice, true),
    [formData.stopLossPrice, slValueType, basePrice, formData.sltpType, rowData?.netQuantity]
  );
  const tpActualPrice = React.useMemo(() =>
    calculateActualPrice(formData.targetPrice, tpValueType, basePrice, false),
    [formData.targetPrice, tpValueType, basePrice, formData.sltpType, rowData?.netQuantity]
  );

  const trailRefActualPrice = React.useMemo(() => {
    if (formData.trailReferencePoints === null || formData.trailReferencePoints === undefined || formData.trailReferencePoints === '') {
      return null;
    }
    const val = parseFloat(formData.trailReferencePoints);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    if (trailValueType === 1) {
      return base * val / 100;
    } else {
      return val;
    }
  }, [formData.trailReferencePoints, trailValueType, basePrice]);

  const trailStepActualPrice = React.useMemo(() => {
    if (formData.trailStepPoints === null || formData.trailStepPoints === undefined || formData.trailStepPoints === '') {
      return null;
    }
    const val = parseFloat(formData.trailStepPoints);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    if (trailValueType === 1) {
      return base * val / 100;
    } else {
      return val;
    }
  }, [formData.trailStepPoints, trailValueType, basePrice]);

  const onClickSlType = () => {
    const basis = formData?.sltpType;
    const newSlValueType = basis === "PNL" ?
      cycleWithin(slValueType, [0, 2]) :
      cycleWithin(slValueType, [0, 1, 2]);

    setSlValueType(newSlValueType);
    isValidsltp("sl", formData?.stopLossPrice, newSlValueType);
  };

  const onClickTpType = () => {
    const basis = formData?.sltpType;
    const newTpValueType = basis === "PNL" ?
      cycleWithin(tpValueType, [0, 2]) :
      cycleWithin(tpValueType, [0, 1, 2]);

    setTpValueType(newTpValueType);
    isValidsltp("tp", formData?.targetPrice, newTpValueType);
  };
  const onClickTrailType = () => {
    const basis = formData?.sltpType;
    if (basis === "PNL" || basis === "Underlying") {
      setTrailValueType(2);
      return;
    }
    setTrailValueType(prev => cycleWithin(prev, [2, 1]));
  };

  const isUnderlyingBasis = formData?.sltpType === "Underlying";

  const validateTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;

    if (atVal !== null && slVal === null) {
      setErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }

    setErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  useEffect(() => {
    if (!show || !rowData || !formData || Object.keys(formData).length === 0) return;

    if (formData.stopLossPrice !== null && formData.stopLossPrice !== undefined && formData.stopLossPrice !== '') {
      isValidsltp("sl", parseFloat(formData.stopLossPrice));
    }

    if (formData.targetPrice !== null && formData.targetPrice !== undefined && formData.targetPrice !== '') {
      isValidsltp("tp", parseFloat(formData.targetPrice));
    }

    validateTrailSL(formData.trailReferencePoints, formData.trailStepPoints);

    validateProfitLock(formData.profitLockAt, formData.profitLockSl);

    validateProfitLockTrail(formData.profitLockTrailReferencePoints, formData.profitLockTrailStepPoints);
  }, [show, formData, slValueType, tpValueType, trailValueType, rowData]);

  const handleFieldChange = (val, name) => {
    if (name === "sltpType") {
      if (formData.sltpType === val) {
        setFormData({ ...formData, sltpType: val });
      } else {
        setFormData({
          "sltpType": val,
          "stopLossPrice": null,
          "targetPrice": null,
          "trailReferencePoints": null,
          "trailStepPoints": null,
        });
        setSlValueType(0);
        setTpValueType(0);
        setTrailValueType(2);
        setSlOperator(2);
        setTargetOperator(0);
        setUnderlyingType(0);
        setError("");
        setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
      }
    } else {
      let value = {
        ...formData,
        [name]: !val && val !== 0 ? null : parseFloat(val),
      };

      if (name === "stopLossPrice" && (!val || val === 0 || val === null)) {
        value.trailReferencePoints = null;
        value.trailStepPoints = null;
        setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
      }

      if ((name === "profitLockAt" || name === "profitLockSl") && (!val || val === 0 || val === null)) {
        value.profitLockTrailReferencePoints = null;
        value.profitLockTrailStepPoints = null;
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
      }

      setFormData(value);

      if (name === "stopLossPrice") {
        isValidsltp("sl", val);
      }
      if (name === "targetPrice") {
        isValidsltp("tp", val);
      }

      if (name === "trailReferencePoints" || name === "trailStepPoints") {
        const newRef = name === "trailReferencePoints" ? value.trailReferencePoints : formData.trailReferencePoints;
        const newStep = name === "trailStepPoints" ? value.trailStepPoints : formData.trailStepPoints;
        validateTrailSL(newRef, newStep);
      }

      if (name === "profitLockAt" || name === "profitLockSl") {
        const newAt = name === "profitLockAt" ? value.profitLockAt : formData.profitLockAt;
        const newSl = name === "profitLockSl" ? value.profitLockSl : formData.profitLockSl;
        validateProfitLock(newAt, newSl);
      }

      if (name === "profitLockTrailReferencePoints" || name === "profitLockTrailStepPoints") {
        const newRef = name === "profitLockTrailReferencePoints" ? value.profitLockTrailReferencePoints : formData.profitLockTrailReferencePoints;
        const newStep = name === "profitLockTrailStepPoints" ? value.profitLockTrailStepPoints : formData.profitLockTrailStepPoints;
        validateProfitLockTrail(newRef, newStep);
      }
    }
  };

  const clearField = (name) => {
    let updatedFormData = {
      ...formData,
      [name]: null
    };

    if (name === "stopLossPrice") {
      updatedFormData.trailReferencePoints = null;
      updatedFormData.trailStepPoints = null;
      setErrorMessages(prev => ({
        ...prev,
        stopLoss: "",
        trailSL: ""
      }));
    }
    else if (name === "profitLockAt" || name === "profitLockSl") {
      updatedFormData.profitLockTrailReferencePoints = null;
      updatedFormData.profitLockTrailStepPoints = null;
      setErrorMessages(prev => ({
        ...prev,
        profitLockTrail: ""
      }));
    }
    else if (name === "targetPrice") {
      setErrorMessages(prev => ({
        ...prev,
        target: ""
      }));
    }
    else if (name === "trailReferencePoints") {
      const otherValue = updatedFormData.trailStepPoints;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateTrailSL(null, otherValue);
      } else {
        setErrorMessages(prev => ({ ...prev, trailSL: "" }));
      }
    }
    else if (name === "trailStepPoints") {
      const otherValue = updatedFormData.trailReferencePoints;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateTrailSL(otherValue, null);
      } else {
        setErrorMessages(prev => ({ ...prev, trailSL: "" }));
      }
    }
    else if (name === "profitLockTrailReferencePoints") {
      const otherValue = formData.profitLockTrailStepPoints;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateProfitLockTrail(null, otherValue);
      } else {
        setErrorMessages(prev => ({ ...prev, profitLockTrail: "" }));
      }
    }
    else if (name === "profitLockTrailStepPoints") {
      const otherValue = formData.profitLockTrailReferencePoints;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateProfitLockTrail(otherValue, null);
      } else {
        setErrorMessages(prev => ({ ...prev, profitLockTrail: "" }));
      }
    }

    setFormData(updatedFormData);
  };

  const updatePositionFun = async (value, positionID) => {
    const norm = (n) => {
      if (n === 0) return 0;
      if (n === undefined || n === null || n === "") return null;
      const p = parseFloat(n);
      return Number.isNaN(p) ? null : p;
    };

    const isTrailNull = (ref, step) => {
      const refVal = norm(ref);
      const stepVal = norm(step);
      return refVal === null && stepVal === null;
    };

    const basis = formData?.sltpType;
    const basisValue = basis === "Underlying" ? 2 : (basis === "PNL" ? 0 : 1);

    if (basisValue === 2) {
      const payload = {
        rms: {
          basis: 2,
          underlyingType: underlyingType,
          targetInput: norm(value.targetPrice) === null ? null : {
            valueType: 0,
            value: norm(value.targetPrice)
          },
          targetOperator: targetOperator,
          stopLossInput: norm(value.stopLossPrice) === null ? null : {
            valueType: 0,
            value: norm(value.stopLossPrice)
          },
          stopLossOperator: slOperator,
        }
      };

      if (rowData.netQuantity !== 0) {
        await dispatch(updatePositionAction(positionID, payload));
      }
      return;
    }

    const payload = {
      rms: {
        basis: basisValue,
        targetInput: norm(value.targetPrice) === null ? null : {
          valueType: basis === "PNL" ? (tpValueType ?? 0) : (tpValueType ?? 0),
          value: norm(value.targetPrice)
        },
        stopLossInput: norm(value.stopLossPrice) === null ? null : {
          valueType: basis === "PNL" ? (slValueType ?? 0) : (slValueType ?? 0),
          value: norm(value.stopLossPrice)
        },
        trailSL: isTrailNull(value.trailReferencePoints, value.trailStepPoints) ? null : {
          valueType: (trailValueType === 1 ? 1 : 2),
          reference: norm(value.trailReferencePoints),
          step: norm(value.trailStepPoints)
        },
        profitLockAt: norm(value.profitLockAt),
        profitLockSl: norm(value.profitLockSl),
        profitLockTrail: isTrailNull(value.profitLockTrailReferencePoints, value.profitLockTrailStepPoints) ? null : {
          valueType: 2,
          reference: norm(value.profitLockTrailReferencePoints),
          step: norm(value.profitLockTrailStepPoints)
        }
      }
    };

    if (rowData.netQuantity !== 0) {
      await dispatch(updatePositionAction(positionID, payload));
    }
  };

  const handleSave = async () => {
    if (!isValidsltp("sl", formData.stopLossPrice)) return;
    if (!isValidsltp("tp", formData.targetPrice)) return;

    if (!isUnderlyingBasis) {
      if (!validateTrailSL(formData.trailReferencePoints, formData.trailStepPoints)) return;
      if (!validateProfitLock(formData.profitLockAt, formData.profitLockSl)) return;
      if (!validateProfitLockTrail(formData.profitLockTrailReferencePoints, formData.profitLockTrailStepPoints)) return;
    }

    const value = { ...formData };

    if (!value.stopLossPrice) {
      value.trailReferencePoints = null;
      value.trailStepPoints = null;
      value.stopLossPrice = value.stopLossPrice === 0 ? 0 : null;
    } else {
      if (value.trailReferencePoints && value.trailStepPoints) {
        value.trailReferencePoints = parseFloat(value.trailReferencePoints);
        value.trailStepPoints = parseFloat(value.trailStepPoints);
      } else if (value.trailReferencePoints && !value.trailStepPoints) {
        value.trailReferencePoints = parseFloat(value.trailReferencePoints);
        value.trailStepPoints = parseFloat(value.trailReferencePoints);
      } else if (value.trailStepPoints && !value.trailReferencePoints) {
        value.trailReferencePoints = parseFloat(value.trailStepPoints);
        value.trailStepPoints = parseFloat(value.trailStepPoints);
      } else {
        value.trailReferencePoints = null;
        value.trailStepPoints = null;
      }
    }

    value.stopLossPrice = value.stopLossPrice || value.stopLossPrice === 0 ? parseFloat(value.stopLossPrice) : null;
    value.targetPrice = value.targetPrice || value.targetPrice === 0 ? parseFloat(value.targetPrice) : null;

    await updatePositionFun(value, rowData.id);
    setShow(false);
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="sm"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="fixed-modal-sltp"
    >
      <Modal.Header style={{ position: 'relative' }}>
        <Modal.Title className="modal-title-size" id="contained-modal-title-vcenter">
          {rowData?.instrument?.displayName || rowData?.instrument?.tradingSymbol || 'Position'}
          {rowData?.netQuantity > 0 && (
            <span className="ms-2 text-primary">(Long)</span>
          )}
          {rowData?.netQuantity < 0 && (
            <span className="ms-2 text-danger">(Short)</span>
          )}
          {' - Risk Settings'}
        </Modal.Title>
        <button type="button" className="modal-close-button" onClick={() => setShow(false)} aria-label="Close">×</button>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-sltp">
          <div style={{ justifyContent: "space-between" }} className="sltp-div modal-input">
            <Form.Group className="form-group">
              <label className="form-label">SL/TP Basis</label>
              <Form.Select
                aria-label="Default select example"
                name="sltpType"
                value={formData.sltpType}
                onChange={(e) => handleFieldChange(e.target.value, "sltpType")}
              >
                {defSLTP.map((data, index) => {
                  return (
                    <option value={data} key={index}>
                      {data}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
            <div className="pnl-com-p" style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              <p className="m-0">LTP: <span id={`ltp-${rowData?.instrumentToken}-${rowData?.basketId || ''}`}>{ltp || '0.00'}</span></p>
              <p className="m-0">PNL: <span id={`pnl-${rowData?.instrumentToken}-${rowData?.basketId || ''}`} className={currentPnl >= 0 ? "text-success" : "text-danger"}>{fmtINR(currentPnl)}</span></p>
            </div>
          </div>

          {isUnderlyingBasis && (
            <div className="sltp-div modal-input" style={{ marginTop: '8px' }}>
              <Form.Group className="form-group">
                <label className="form-label">Underlying Type</label>
                <Form.Select
                  value={underlyingType}
                  onChange={(e) => setUnderlyingType(parseInt(e.target.value))}
                >
                  {UNDERLYING_TYPES.map((ut) => (
                    <option value={ut.value} key={ut.value}>
                      {ut.label}{ut.disabled ? " (coming soon)" : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          <div className={isUnderlyingBasis ? "sltp-div flex-column gap-3 w-100" : "sltp-div"}>
            <div className={isUnderlyingBasis ? "w-100" : "sltp-col-left"}>
              <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                Stop Loss
                {slActualPrice !== null && (slValueType === 1 || slValueType === 2) && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                    (₹{slActualPrice.toFixed(2)})
                  </span>
                )}
              </Form.Label>
              <div className="sltp-input-row">
                {isUnderlyingBasis && (
                  <Form.Select
                    style={{ width: '60px', minWidth: '60px', padding: '2px 4px', fontSize: '12px', marginRight: '4px' }}
                    value={slOperator}
                    onChange={(e) => setSlOperator(parseInt(e.target.value))}
                  >
                    {OPERATOR_OPTIONS.map(op => (
                      <option value={op.value} key={op.value}>{op.label}</option>
                    ))}
                  </Form.Select>
                )}
                <div className={errorMessages.stopLoss ? "error-border" : ""}>
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "stopLossPrice")}
                    filterValue={formData.stopLossPrice !== null && formData.stopLossPrice !== undefined ? Number(formData.stopLossPrice).toFixed(2) : null}
                    name={"stopLossPrice"}
                    step={0.05}
                    formatINR={true}
                  />
                </div>
                {isUnderlyingBasis ? (
                  <div className="pts-class">₹</div>
                ) : formData?.sltpType === "PNL" ? (
                  <div className="pts-class" onClick={onClickSlType}>{renderPNLSLTPLabel(slValueType)}</div>
                ) : (
                  <div className="pts-class" onClick={onClickSlType}>{renderTypeLabel(slValueType)}</div>
                )}
                <X size={16} onClick={() => clearField("stopLossPrice")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
              </div>
              {errorMessages.stopLoss && (
                <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                  {errorMessages.stopLoss}
                </div>
              )}
            </div>
            <div className={isUnderlyingBasis ? "w-100" : "sltp-col-right"}>
              <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                Target
                {!isUnderlyingBasis && tpActualPrice !== null && (tpValueType === 1 || tpValueType === 2) && (
                  <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                    (₹{tpActualPrice.toFixed(2)})
                  </span>
                )}
              </Form.Label>
              <div className="sltp-input-row">
                {isUnderlyingBasis && (
                  <Form.Select
                    style={{ width: '60px', minWidth: '60px', padding: '2px 4px', fontSize: '12px', marginRight: '4px' }}
                    value={targetOperator}
                    onChange={(e) => setTargetOperator(parseInt(e.target.value))}
                  >
                    {OPERATOR_OPTIONS.map(op => (
                      <option value={op.value} key={op.value}>{op.label}</option>
                    ))}
                  </Form.Select>
                )}
                <div className={errorMessages.target ? "error-border" : ""}>
                  <PlusMinQty
                    qtyClick={(qty) => handleFieldChange(qty, "targetPrice")}
                    filterValue={formData.targetPrice !== null && formData.targetPrice !== undefined ? Number(formData.targetPrice).toFixed(2) : null}
                    name={"targetPrice"}
                    step={0.05}
                    formatINR={true}
                  />
                </div>
                {isUnderlyingBasis ? (
                  <div className="pts-class">₹</div>
                ) : formData?.sltpType === "PNL" ? (
                  <div className="pts-class" onClick={onClickTpType}>{renderPNLSLTPLabel(tpValueType)}</div>
                ) : (
                  <div className="pts-class" onClick={onClickTpType}>{renderTypeLabel(tpValueType)}</div>
                )}
                <X size={16} onClick={() => clearField("targetPrice")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
              </div>
              {errorMessages.target && (
                <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                  {errorMessages.target}
                </div>
              )}
            </div>
          </div>

          {!isUnderlyingBasis && (
            <>
              <div className="sltp-div-profile">
                <p className="sltp-trail-header">Stoploss Trailing</p>
                <div className="sltp-div">
                  <div className="sltp-col-left">
                    <Form.Label className="sltp-label-above">
                      For every X move
                      {trailRefActualPrice !== null && trailValueType === 1 && (
                        <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                          (₹{trailRefActualPrice.toFixed(2)})
                        </span>
                      )}
                    </Form.Label>
                    <div className="sltp-input-row">
                      <PlusMinQty
                        qtyClick={(qty) => handleFieldChange(qty, "trailReferencePoints")}
                        filterValue={formData.trailReferencePoints ?? null}
                        name={"trailReferencePoints"}
                        min={0.05}
                        step={0.05}
                        disabled={formData.stopLossPrice === null}
                        formatINR={true}
                      />
                      {formData?.sltpType === "PNL" ? (
                        <div className="pts-class">{renderPNLLabel()}</div>
                      ) : (
                        <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                      )}
                      <X
                        size={16}
                        onClick={() => clearField("trailReferencePoints")}
                        className={`sltp-delete-icon ${(formData.stopLossPrice === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                  <div className="sltp-col-right">
                    <Form.Label className="sltp-label-above">
                      Trail SL by Y
                      {trailStepActualPrice !== null && trailValueType === 1 && (
                        <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                          (₹{trailStepActualPrice.toFixed(2)})
                        </span>
                      )}
                    </Form.Label>
                    <div className="sltp-input-row">
                      <PlusMinQty
                        qtyClick={(qty) => handleFieldChange(qty, "trailStepPoints")}
                        filterValue={formData.trailStepPoints ?? null}
                        name={"trailStepPoints"}
                        min={0.05}
                        step={0.05}
                        disabled={formData.stopLossPrice === null}
                        formatINR={true}
                      />
                      {formData?.sltpType === "PNL" ? (
                        <div className="pts-class">{renderPNLLabel()}</div>
                      ) : (
                        <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                      )}
                      <X
                        size={16}
                        onClick={() => clearField("trailStepPoints")}
                        className={`sltp-delete-icon ${(formData.stopLossPrice === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
                {errorMessages.trailSL && (
                  <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                    {errorMessages.trailSL}
                  </div>
                )}
              </div>
              <div className="sltp-div-profile">
                <p className="sltp-trail-header">Profit Locking</p>
                <div className="sltp-div">
                  <div className="sltp-col-left">
                    <Form.Label className="sltp-label-above">When profit reaches</Form.Label>
                    <div className="sltp-input-row">
                      <PlusMinQty
                        qtyClick={(qty) => handleFieldChange(qty, "profitLockAt")}
                        filterValue={formData.profitLockAt ?? null}
                        name={"profitLockAt"}
                        min={0.05}
                        step={0.05}
                        formatINR={true}
                      />
                      <div className="pts-class">{renderPNLLabel()}</div>
                      <X size={16} onClick={() => clearField("profitLockAt")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                    </div>
                  </div>
                  <div className="sltp-col-right">
                    <Form.Label className="sltp-label-above">Lock minimum profit at</Form.Label>
                    <div className="sltp-input-row">
                      <PlusMinQty
                        qtyClick={(qty) => handleFieldChange(qty, "profitLockSl")}
                        filterValue={formData.profitLockSl ?? null}
                        name={"profitLockSl"}
                        min={0.05}
                        step={0.05}
                        formatINR={true}
                      />
                      <div className="pts-class">{renderPNLLabel()}</div>
                      <X size={16} onClick={() => clearField("profitLockSl")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                    </div>
                  </div>
                </div>
                {errorMessages.profitLock && (
                  <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                    {errorMessages.profitLock}
                  </div>
                )}
              </div>
              <div className="sltp-div-profile">
                <p className="sltp-trail-header">Profit Trailing</p>
                <div className="sltp-div">
                  <div className="sltp-col-left">
                    <Form.Label className="sltp-label-above">If profit increases by X</Form.Label>
                    <div className="sltp-input-row">
                      <PlusMinQty
                        qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailReferencePoints")}
                        filterValue={formData.profitLockTrailReferencePoints ?? null}
                        name={"profitLockTrailReferencePoints"}
                        min={0.05}
                        step={0.05}
                        formatINR={true}
                        disabled={formData.profitLockAt === null || formData.profitLockSl === null}
                      />
                      <div className="pts-class">{renderPNLLabel()}</div>
                      <X
                        size={16}
                        onClick={() => clearField("profitLockTrailReferencePoints")}
                        className={`sltp-delete-icon ${(formData.profitLockAt === null || formData.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                  <div className="sltp-col-right">
                    <Form.Label className="sltp-label-above">Trail Profit by Y</Form.Label>
                    <div className="sltp-input-row">
                      <PlusMinQty
                        qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailStepPoints")}
                        filterValue={formData.profitLockTrailStepPoints ?? null}
                        name={"profitLockTrailStepPoints"}
                        min={0.05}
                        step={0.05}
                        formatINR={true}
                        disabled={formData.profitLockAt === null || formData.profitLockSl === null}
                      />
                      <div className="pts-class">{renderPNLLabel()}</div>
                      <X
                        size={16}
                        onClick={() => clearField("profitLockTrailStepPoints")}
                        className={`sltp-delete-icon ${(formData.profitLockAt === null || formData.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </div>
                {errorMessages.profitLockTrail && (
                  <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                    {errorMessages.profitLockTrail}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Summary Section */}
          <div className="sltp-summary-section">
            <div className="sltp-summary-title">
              Summary
            </div>
            <div className="sltp-summary-body">
              {(() => {
                const summaryLines = [];
                if (formData?.sltpType === "Underlying") {
                  const utLabel = UNDERLYING_TYPES.find(u => u.value === underlyingType)?.label || "Spot";
                  if (formData.stopLossPrice !== null && formData.stopLossPrice !== undefined && formData.stopLossPrice !== '') {
                    summaryLines.push(`SL fires when underlying ${utLabel} LTP ${operatorLabel(slOperator)} ₹${parseFloat(formData.stopLossPrice).toFixed(2)}`);
                  }
                  if (formData.targetPrice !== null && formData.targetPrice !== undefined && formData.targetPrice !== '') {
                    summaryLines.push(`Target fires when underlying ${utLabel} LTP ${operatorLabel(targetOperator)} ₹${parseFloat(formData.targetPrice).toFixed(2)}`);
                  }
                  if (summaryLines.length === 0) {
                    return <div style={{ color: '#6c757d', fontStyle: 'italic' }}>No risk settings configured</div>;
                  }
                  return summaryLines.map((line, index) => (
                    <div key={index} style={{ marginBottom: index < summaryLines.length - 1 ? '8px' : '0' }}>{line}</div>
                  ));
                }

                const basisLabel = formData?.sltpType === "PNL" ? "PnL" : "price";
                const isLongPosition = rowData?.netQuantity > 0;

                // Stop Loss
                if (formData.stopLossPrice !== null && formData.stopLossPrice !== undefined && formData.stopLossPrice !== '') {
                  const slValue = parseFloat(formData.stopLossPrice);
                  let slText = '';
                  if (formData?.sltpType === "PNL") {
                    if (slValueType === 0) {
                      slText = `₹${Math.abs(slValue).toFixed(2)}`;
                    } else {
                      // For Pts, show actual price
                      if (slActualPrice !== null) {
                        slText = `₹${Math.abs(slActualPrice).toFixed(2)}`;
                      } else {
                        slText = `${Math.abs(slValue).toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} falls to ${slText}, the position will exit.`);
                  } else {
                    let slDisplay = '';
                    if (slValueType === 0) {
                      slDisplay = `₹${slValue.toFixed(2)}`;
                    } else if (slValueType === 1 || slValueType === 2) {
                      // For % and Pts, show actual price
                      if (slActualPrice !== null) {
                        slDisplay = `₹${slActualPrice.toFixed(2)}`;
                      } else {
                        slDisplay = slValueType === 1 ? `${slValue.toFixed(2)}%` : `${slValue.toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} ${isLongPosition ? 'falls to' : 'rises to'} ${slDisplay}, the position will exit.`);
                  }
                }

                // Target
                if (formData.targetPrice !== null && formData.targetPrice !== undefined && formData.targetPrice !== '') {
                  const tpValue = parseFloat(formData.targetPrice);
                  let tpText = '';
                  if (formData?.sltpType === "PNL") {
                    if (tpValueType === 0) {
                      tpText = `₹${Math.abs(tpValue).toFixed(2)}`;
                    } else {
                      // For Pts, show actual price
                      if (tpActualPrice !== null) {
                        tpText = `₹${Math.abs(tpActualPrice).toFixed(2)}`;
                      } else {
                        tpText = `${Math.abs(tpValue).toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} rises to ${tpText}, the position will exit with profit.`);
                  } else {
                    let tpDisplay = '';
                    if (tpValueType === 0) {
                      tpDisplay = `₹${tpValue.toFixed(2)}`;
                    } else if (tpValueType === 1 || tpValueType === 2) {
                      // For % and Pts, show actual price
                      if (tpActualPrice !== null) {
                        tpDisplay = `₹${tpActualPrice.toFixed(2)}`;
                      } else {
                        tpDisplay = tpValueType === 1 ? `${tpValue.toFixed(2)}%` : `${tpValue.toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} ${isLongPosition ? 'rises to' : 'falls to'} ${tpDisplay}, the position will exit with profit.`);
                  }
                }

                // Stoploss Trailing
                if ((formData.trailReferencePoints !== null && formData.trailReferencePoints !== undefined && formData.trailReferencePoints !== '') &&
                  (formData.trailStepPoints !== null && formData.trailStepPoints !== undefined && formData.trailStepPoints !== '')) {
                  const refValue = parseFloat(formData.trailReferencePoints);
                  const stepValue = parseFloat(formData.trailStepPoints);
                  let refText = '';
                  let stepText = '';

                  if (formData?.sltpType === "PNL") {
                    refText = `₹${refValue.toFixed(2)}`;
                    stepText = `₹${stepValue.toFixed(2)}`;
                  } else {
                    if (trailValueType === 1) {
                      // For %, show actual price if available
                      if (trailRefActualPrice !== null && trailStepActualPrice !== null) {
                        refText = `₹${trailRefActualPrice.toFixed(2)}`;
                        stepText = `₹${trailStepActualPrice.toFixed(2)}`;
                      } else {
                        refText = `${refValue.toFixed(2)}%`;
                        stepText = `${stepValue.toFixed(2)}%`;
                      }
                    } else {
                      refText = `₹${refValue.toFixed(2)}`;
                      stepText = `₹${stepValue.toFixed(2)}`;
                    }
                  }
                  summaryLines.push(`For every ${refText} increase in profit, SL will move up by ${stepText}.`);
                }

                // Profit Locking
                if ((formData.profitLockAt !== null && formData.profitLockAt !== undefined && formData.profitLockAt !== '') &&
                  (formData.profitLockSl !== null && formData.profitLockSl !== undefined && formData.profitLockSl !== '')) {
                  const atValue = parseFloat(formData.profitLockAt);
                  const slValue = parseFloat(formData.profitLockSl);
                  summaryLines.push(`When profit reaches ₹${atValue.toFixed(2)}, at least ₹${slValue.toFixed(2)} will be protected.`);
                }

                // Profit Trailing
                if ((formData.profitLockTrailReferencePoints !== null && formData.profitLockTrailReferencePoints !== undefined && formData.profitLockTrailReferencePoints !== '') &&
                  (formData.profitLockTrailStepPoints !== null && formData.profitLockTrailStepPoints !== undefined && formData.profitLockTrailStepPoints !== '')) {
                  const refValue = parseFloat(formData.profitLockTrailReferencePoints);
                  const stepValue = parseFloat(formData.profitLockTrailStepPoints);
                  summaryLines.push(`Each time profit increases by ₹${refValue.toFixed(2)}, locked profit will rise by ₹${stepValue.toFixed(2)}.`);
                }

                if (summaryLines.length === 0) {
                  return <div style={{ color: '#6c757d', fontStyle: 'italic' }}>No risk settings configured</div>;
                }

                return summaryLines.map((line, index) => (
                  <div key={index} style={{ marginBottom: index < summaryLines.length - 1 ? '8px' : '0' }}>
                    {line}
                  </div>
                ));
              })()}
            </div>
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleReset} variant="outline-primary">Reset</Button>
        <Button disabled={!!errorMessages.stopLoss || !!errorMessages.target || !!errorMessages.trailSL || !!errorMessages.profitLock || !!errorMessages.profitLockTrail} onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal >
  );
};

export default LegSLTPModal;